/*
  Fumetsu tiered kit: shared pure logic.
  One source for the storefront builder (window.FumetsuKitTiers), the local-only discount
  Function in functions/fumetsu-kit-discount and tests/tiered-kit.test.cjs.
  Money is always whole minor units (Shopify Liquid and Ajax hundredths). No DOM, no network.
*/
(function (root, factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.FumetsuKitTiers = api;
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';

  // Exact product types that count as training kit. Kept in sync with
  // templates/collection.kit-products.liquid (the test enforces it).
  var TRAINING_TYPES = [
    'Boxing Gloves', 'Shin Guards', 'Rash Guards', 'Fight Shorts', 'BJJ Gis', 'BJJ Belts', 'MMA Gloves',
    'Muay Thai Shorts', 'Spats', 'Head Guards', 'Focus Mitts', 'Focus Paddles', 'Punch Bags', 'Body Protector', 'Belly/Body Pads',
    'Compression Shorts', 'Groin Guards', 'Thai Pads', 'Kick Shields', 'Grappling Dummies'
  ];
  // The feed's allowlist, kept in sync with templates/collection.kit-products.liquid (the test
  // enforces it): training kit plus the types that are only ever accessories.
  var CATALOGUE_TYPES = TRAINING_TYPES.concat(['Hand Wraps', 'Socks']);
  var REWARD_TYPES = ['percentage', 'fixed_amount'];
  var PRODUCT_GID = /^gid:\/\/shopify\/Product\/\d+$/;

  // Merchant default: disabled, four thresholds, zero rewards, no price floor.
  function defaultConfig() {
    return {
      version: 1,
      enabled: false,
      currency: 'GBP',
      minimumUnitPrice: 0,
      tiers: [2, 3, 4, 5].map(function (count) {
        return { count: count, type: 'percentage', value: 0 };
      })
    };
  }

  function tierErrors(tiers) {
    if (!Array.isArray(tiers) || !tiers.length) return ['tiers must be a non-empty list'];
    var errors = [];
    var previous = null;
    tiers.forEach(function (tier, index) {
      var name = 'tier ' + (index + 1);
      if (!tier || typeof tier !== 'object') {
        errors.push(name + ' must be an object');
        return;
      }
      if (!Number.isInteger(tier.count) || tier.count < 2) errors.push(name + ' needs a whole item count of 2 or more');
      if (REWARD_TYPES.indexOf(tier.type) < 0) {
        errors.push(name + ' reward type must be percentage or fixed_amount');
      } else if (tier.type === 'percentage') {
        if (!(typeof tier.value === 'number' && tier.value >= 0 && tier.value <= 100)) errors.push(name + ' percentage must be a number from 0 to 100');
      } else if (!(Number.isSafeInteger(tier.value) && tier.value >= 0)) {
        errors.push(name + ' fixed amount must be whole minor units');
      }
      if (previous) {
        if (!(tier.count > previous.count)) errors.push(name + ' item count must be higher than the tier before');
        // ponytail: one reward type across all tiers so rewards compare; allow mixing only with a real need.
        if (tier.type !== previous.type) errors.push('all tiers must use the same reward type');
        else if (tier.value < previous.value) errors.push(name + ' reward must not be lower than the tier before');
      }
      previous = tier;
    });
    return errors;
  }

  // Central validator for the theme settings and the Function metafield (same shape; the
  // Function adds productIds). Anything invalid is blocked and replaced by the disabled default.
  // minimumUnitPrice is the price floor in minor units of `currency`; absent means 0 (no floor).
  function normalizeConfig(raw) {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
      return { ok: false, errors: ['config must be an object'], config: defaultConfig() };
    }
    var errors = [];
    var floor = raw.minimumUnitPrice === undefined ? 0 : raw.minimumUnitPrice;
    if (typeof raw.enabled !== 'boolean') errors.push('enabled must be true or false');
    if (typeof raw.currency !== 'string' || !/^[A-Z]{3}$/.test(raw.currency)) errors.push('currency must be a three letter ISO code');
    if (!(Number.isSafeInteger(floor) && floor >= 0)) errors.push('minimumUnitPrice must be whole minor units of 0 or more');
    errors = errors.concat(tierErrors(raw.tiers));
    if (raw.productIds !== undefined && !(Array.isArray(raw.productIds) && raw.productIds.every(function (id) { return PRODUCT_GID.test(id); }))) {
      errors.push('productIds must be a list of Shopify product GIDs');
    }
    // Optional Function-only accessory list: rewarded at the kit rate but never counted, so it
    // must not overlap the training allowlist.
    if (raw.accessoryProductIds !== undefined) {
      if (!(Array.isArray(raw.accessoryProductIds) && raw.accessoryProductIds.every(function (id) { return PRODUCT_GID.test(id); }))) {
        errors.push('accessoryProductIds must be a list of Shopify product GIDs');
      } else if (Array.isArray(raw.productIds) && raw.accessoryProductIds.some(function (id) { return raw.productIds.indexOf(id) >= 0; })) {
        errors.push('accessoryProductIds must not overlap productIds');
      }
    }
    if (errors.length) return { ok: false, errors: errors, config: defaultConfig() };

    var config = {
      version: 1,
      enabled: raw.enabled,
      currency: raw.currency,
      minimumUnitPrice: floor,
      tiers: raw.tiers.map(function (tier) {
        return { count: tier.count, type: tier.type, value: tier.value };
      })
    };
    if (raw.productIds) config.productIds = raw.productIds.slice();
    if (raw.accessoryProductIds) config.accessoryProductIds = raw.accessoryProductIds.slice();
    return { ok: true, errors: [], config: config };
  }

  function isTrainingType(type) {
    return typeof type === 'string' && TRAINING_TYPES.indexOf(type) >= 0;
  }

  function isSale(line) {
    return Number.isSafeInteger(line.compareAtPrice) && line.compareAtPrice > line.price;
  }

  function validLine(line) {
    return Boolean(line) && line.productId != null && line.productId !== '' &&
      Number.isSafeInteger(line.price) && line.price >= 0 &&
      Number.isInteger(line.quantity) && line.quantity > 0;
  }

  // A positive price floor only means something in its own currency: no FX guess.
  function floorMismatch(config, currency) {
    return Boolean(config) && config.minimumUnitPrice > 0 && currency !== config.currency;
  }

  // Counting toward a tier. Eligibility is a strict boolean set from real product data. Sale
  // training kit still counts once; accessories, free lines and units under the floor never do.
  function qualifies(line, config, currency) {
    if (!(validLine(line) && line.qualifying === true && line.accessory !== true && line.price > 0)) return false;
    var floor = (config && config.minimumUnitPrice) || 0;
    return !floor || (!floorMismatch(config, currency) && line.price >= floor);
  }

  // Whether one unit of the line takes the reward: counted training kit or a strict-boolean
  // accessory, never a sale or free line. An accessory skips the floor because it never counts.
  function rewardable(line, config, currency) {
    if (!validLine(line) || !(line.price > 0) || isSale(line)) return false;
    return line.accessory === true ? !floorMismatch(config, currency) : qualifies(line, config, currency);
  }

  // Whether a tier's reward can exist in this currency. The storefront uses it for reward copy.
  function rewardAvailable(config, tier, currency) {
    if (!config.enabled || !tier.value || floorMismatch(config, currency)) return false;
    return tier.type === 'percentage' || currency === config.currency;
  }

  // lines: [{ productId, variantId, quantity, price, compareAtPrice, qualifying, accessory }]
  // qualifyingCount is the counted products; targets and qualifyingTotal are the rewarded units.
  // Standard prices are always summed exactly; a discount only exists when the config is valid,
  // enabled and the highest reached tier has a reward. A percentage is floored per rewarded
  // unit, as native checkout does, so the projection never promises more than checkout gives.
  function evaluateKit(lines, rawConfig, currency) {
    var norm = normalizeConfig(rawConfig);
    var config = norm.config;
    var counted = Object.create(null);
    var count = 0;
    var byProduct = Object.create(null);
    var targets = [];
    var standardTotal = 0;
    var overflow = false;

    (lines || []).forEach(function (line) {
      if (!validLine(line)) return;
      var lineTotal = line.price * line.quantity;
      // Never sum past exact integers: the line is left out and the cart earns no reward.
      if (!Number.isSafeInteger(lineTotal) || !Number.isSafeInteger(standardTotal + lineTotal)) {
        overflow = true;
        return;
      }
      standardTotal += lineTotal;
      // Counted and rewarded sets stay separate: sale training kit counts once but is never
      // discounted again, and a full-price accessory is rewarded without ever counting.
      var key = String(line.productId);
      if (qualifies(line, config, currency) && !(key in counted)) {
        counted[key] = true;
        count += 1;
      }
      if (!rewardable(line, config, currency)) return;
      var unit = { lineId: line.lineId, productId: line.productId, variantId: line.variantId, price: line.price, quantity: 1 };
      // One rewarded unit per product, however many variants or units are in the cart: the cheapest.
      if (!(key in byProduct)) {
        byProduct[key] = targets.length;
        targets.push(unit);
      } else if (unit.price < targets[byProduct[key]].price) {
        targets[byProduct[key]] = unit;
      }
    });

    // Tiers follow the counted products, never the number of reward targets.
    var tier = null;
    var nextTier = null;
    config.tiers.forEach(function (candidate) {
      if (candidate.count <= count) tier = candidate; // highest reached only, never stacked
      else if (!nextTier) nextTier = candidate;
    });

    // Each unit price is part of a line already in standardTotal, so this sum stays exact too.
    var qualifyingTotal = targets.reduce(function (sum, unit) { return sum + unit.price; }, 0);
    var discount = 0;
    var reason = 'applied';
    if (!norm.ok) reason = 'invalid-config';
    else if (overflow) reason = 'overflow';
    else if (!config.enabled) reason = 'disabled';
    else if (floorMismatch(config, currency)) reason = 'currency-mismatch';
    else if (!tier) reason = 'below-threshold';
    else if (!tier.value) reason = 'zero-reward';
    else if (tier.type === 'fixed_amount') {
      if (currency !== config.currency) reason = 'currency-mismatch';
      else discount = Math.min(tier.value, qualifyingTotal);
    } else {
      targets.forEach(function (unit) {
        discount += Math.min(unit.price, Math.floor(unit.price * tier.value / 100));
      });
    }

    return {
      ok: norm.ok,
      errors: norm.errors,
      qualifyingCount: count,
      tier: tier,
      nextTier: nextTier,
      remaining: nextTier ? nextTier.count - count : 0,
      standardTotal: standardTotal,
      qualifyingTotal: qualifyingTotal,
      discount: discount,
      payable: standardTotal - discount,
      applied: discount > 0,
      reason: reason,
      targets: targets
    };
  }

  function findVariant(product, variantId) {
    var variants = (product && product.variants) || [];
    for (var i = 0; i < variants.length; i++) {
      if (String(variants[i].id) === String(variantId)) return variants[i];
    }
    return null;
  }

  // The one kit-or-accessory rule, per variant, for the listing, toLine and the refresh flows.
  // Hand wraps and socks are always accessories. Training kit is kit from the price floor up
  // (the selling price, never compare-at) and an accessory below it. Types outside the
  // catalogue are neither. The floor is never compared in another currency: training stays
  // listed as kit there and qualifies() counts none of it.
  function variantRole(product, variant, config, currency) {
    if (!product || !variant || CATALOGUE_TYPES.indexOf(product.type) < 0) return null;
    if (!isTrainingType(product.type)) return 'accessory';
    var floor = (config && config.minimumUnitPrice) || 0;
    return !floor || floorMismatch(config, currency) || variant.price >= floor ? 'kit' : 'accessory';
  }

  // Builder selection -> line, classified by variantRole. Optional extras (socks) are accessories: whatever their type they
  // never count, but a full-price one takes the kit rate.
  function toLine(product, variantId, extra, config, currency) {
    var variant = findVariant(product, variantId);
    if (!variant) return null;
    var role = variantRole(product, variant, config, currency);
    return {
      productId: product.id,
      variantId: variant.id,
      quantity: 1,
      price: variant.price,
      compareAtPrice: variant.compare_at_price || null,
      qualifying: !extra && role === 'kit',
      accessory: extra === true || role === 'accessory'
    };
  }

  // What changed for a selected variant after a stock and price refresh.
  function variantChange(before, now) {
    if (!now || !now.available) return 'unavailable';
    if (before && (now.price !== before.price || (now.compare_at_price || 0) !== (before.compare_at_price || 0))) return 'price';
    return null;
  }

  // Flattens catalogue pages (and unions of collections) into unique catalogue products.
  function mergeProducts(lists) {
    var seen = Object.create(null);
    var products = [];
    (lists || []).forEach(function (list) {
      (list || []).forEach(function (product) {
        if (!product || seen[product.id] || CATALOGUE_TYPES.indexOf(product.type) < 0) return;
        seen[product.id] = true;
        products.push(product);
      });
    });
    return products;
  }

  function sized(url) {
    return url ? url + (url.indexOf('?') < 0 ? '?' : '&') + 'width=600' : null;
  }

  // /products/<handle>.js -> the shape served by templates/collection.kit-products.liquid.
  function fromAjaxProduct(product, rootPath) {
    return {
      id: product.id,
      handle: product.handle,
      title: product.title,
      type: product.type,
      url: (rootPath || '/') + 'products/' + product.handle,
      image: sized(product.featured_image),
      variants: (product.variants || []).map(function (variant) {
        return {
          id: variant.id,
          title: variant.title,
          price: variant.price,
          compare_at_price: variant.compare_at_price || null,
          available: Boolean(variant.available),
          image: sized(variant.featured_image && variant.featured_image.src)
        };
      })
    };
  }

  // Review line from the real Judge.me badge numbers the feed serves as strings. A real zero
  // says so; anything unreadable gives '' and nothing is shown. Never an invented rating.
  function reviewSummary(rating, count) {
    if (!/^\d+(\.\d+)?$/.test(String(rating)) || !/^\d+$/.test(String(count))) return '';
    rating = Number(rating);
    count = Number(count);
    if (!count) return 'No reviews yet';
    if (!(rating > 0 && rating <= 5)) return '';
    return '★ ' + rating.toFixed(1) + ' (' + count + (count === 1 ? ' review' : ' reviews') + ')';
  }

  // Cart payload: real variant ids, one unit each, nothing else. No parent product, no line
  // item extras, so nothing client-side can be mistaken for a trusted kit marker.
  function cartItems(lines) {
    var seen = Object.create(null);
    var items = [];
    (lines || []).forEach(function (line) {
      var id = Number(line && line.variantId);
      if (!Number.isSafeInteger(id) || id <= 0 || seen[id]) return;
      seen[id] = true;
      items.push({ id: id, quantity: 1 });
    });
    return items;
  }

  // "12.50" -> 1250 by string, never float. More than two significant decimals, or an amount
  // too large to hold exactly, is rejected.
  // ponytail: assumes hundredths for every currency, matching Liquid money; revisit for a three-decimal market.
  function decimalToMinor(value) {
    var match = /^(\d+)(?:\.(\d{1,2})0*)?$/.exec(String(value));
    if (!match) return null;
    var minor = Number(match[1]) * 100 + Number(((match[2] || '') + '00').slice(0, 2));
    return Number.isSafeInteger(minor) ? minor : null;
  }

  function minorToDecimal(minor) {
    return Math.floor(minor / 100) + '.' + ('0' + (minor % 100)).slice(-2);
  }

  // Body of the cart.lines.discounts.generate.run Function. Eligibility comes only from the
  // server-side config metafield and real cart line data, never from line properties.
  function discountOperations(input) {
    var none = { operations: [] };
    var discount = (input && input.discount) || {};
    var cartLines = (input && input.cart && input.cart.lines) || [];
    if ((discount.discountClasses || []).indexOf('PRODUCT') < 0) return none;

    var norm = normalizeConfig(discount.metafield && discount.metafield.jsonValue);
    var allowed = norm.config.productIds || [];
    var accessories = norm.config.accessoryProductIds || [];
    if (!norm.ok || !norm.config.enabled || !allowed.length) return none;
    // A live discount must carry an approved positive price floor, or cheap filler items
    // could buy a higher tier. Zero fails closed.
    if (!(norm.config.minimumUnitPrice > 0)) return none;

    // Every real variant line must be priced in the config currency, the only one the floor
    // means anything in. Nothing is guessed from one line and trusted for the rest.
    // ponytail: pinned to config.currency because the floor above is mandatory; derive the cart
    // currency instead if a floorless discount is ever allowed.
    var currency = norm.config.currency;
    var failed = false;
    var lines = cartLines.map(function (cartLine) {
      var variant = cartLine.merchandise || {};
      var product = variant.product || {};
      var cost = cartLine.cost || {};
      var unit = cost.amountPerQuantity || {};
      var compare = cost.compareAtAmountPerQuantity;
      var price = decimalToMinor(unit.amount);
      var compareAt = compare ? decimalToMinor(compare.amount) : null;
      var real = variant.__typename === 'ProductVariant';
      // Training is the explicit allowlist, the only place a blank product type is accepted.
      var training = real && allowed.indexOf(product.id) >= 0 &&
        (product.productType === '' || product.productType === null || isTrainingType(product.productType));
      // Same split as variantRole, on this line's own selling price: kit from the floor up, an
      // accessory below it.
      var kit = training && price >= norm.config.minimumUnitPrice;
      var line = {
        lineId: cartLine.id,
        productId: product.id,
        variantId: variant.id,
        quantity: cartLine.quantity,
        price: price,
        compareAtPrice: compareAt,
        qualifying: kit,
        // Accessories are cheap training and the trusted accessory list, never line properties.
        accessory: (training && !kit) || (real && accessories.indexOf(product.id) >= 0)
      };
      // One real line with a missing, foreign or unreadable price, compare-at or quantity fails
      // the whole cart closed.
      if (real && (!validLine(line) || unit.currencyCode !== currency ||
        (compare && (compareAt === null || compare.currencyCode !== currency)))) failed = true;
      return line;
    });
    if (failed) return none;

    var result = evaluateKit(lines, norm.config, currency);
    if (!result.applied) return none;
    return {
      operations: [{
        productDiscountsAdd: {
          candidates: [{
            targets: result.targets.map(function (unit) {
              return { cartLine: { id: unit.lineId, quantity: 1 } };
            }),
            message: 'Fumetsu kit (' + result.tier.count + '+ items)',
            value: result.tier.type === 'percentage'
              ? { percentage: { value: result.tier.value } }
              : { fixedAmount: { amount: minorToDecimal(result.discount), appliesToEachItem: false } }
          }],
          selectionStrategy: 'FIRST'
        }
      }]
    };
  }

  return {
    TRAINING_TYPES: TRAINING_TYPES,
    CATALOGUE_TYPES: CATALOGUE_TYPES,
    defaultConfig: defaultConfig,
    normalizeConfig: normalizeConfig,
    isTrainingType: isTrainingType,
    isSale: isSale,
    qualifies: qualifies,
    rewardAvailable: rewardAvailable,
    evaluateKit: evaluateKit,
    findVariant: findVariant,
    variantRole: variantRole,
    toLine: toLine,
    variantChange: variantChange,
    mergeProducts: mergeProducts,
    fromAjaxProduct: fromAjaxProduct,
    cartItems: cartItems,
    reviewSummary: reviewSummary,
    decimalToMinor: decimalToMinor,
    minorToDecimal: minorToDecimal,
    discountOperations: discountOperations
  };
});
